import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import type {
    PestAvgDto,
    PestCodeEnumDto,
    PestDto,
    PestsDto,
    RiskEnumDto,
} from "@station/data/dto/pest.dto";
import {
    PestAvgDtoSchema,
    PestDtoSchema,
    PestsDtoSchema,
} from "@station/data/dto/pest.dto";
import {
    Pest,
    PestAvg,
    PestCodeEnum,
    Pests,
    RiskEnum,
} from "@station/domain/models/pest.model";
import { injectable } from "inversify";
import { err, Result } from "neverthrow";

const PEST_CODE_BY_DTO: Readonly<Record<PestCodeEnumDto, PestCodeEnum>> = {
    araña_roja: PestCodeEnum.ARANA_ROJA,
    erwinia_carotovora: PestCodeEnum.ERWINIA_CAROTOVORA,
    alternaria: PestCodeEnum.ALTERNARIA,
    fusarium: PestCodeEnum.FUSARIUM,
    phytophthora: PestCodeEnum.PHYTOPHTHORA,
    cotonet: PestCodeEnum.COTONET,
    chinche_verde: PestCodeEnum.CHINCHE_VERDE,
    polilla_del_tomate: PestCodeEnum.POLILLA_DEL_TOMATE,
    pulgon: PestCodeEnum.PULGON,
    mosca_blanca: PestCodeEnum.MOSCA_BLANCA,
    thrips_parvispinus: PestCodeEnum.THRIPS_PARVISPINUS,
    manchas_de_humedad: PestCodeEnum.MANCHAS_DE_HUMEDAD,
    pudricion: PestCodeEnum.PUDRICION,
    oidio: PestCodeEnum.OIDIO,
    mildiu: PestCodeEnum.MILDIU,
    botritis: PestCodeEnum.BOTRITIS,
    abiotic_stress: PestCodeEnum.ABIOTIC_STRESS,
};

const RISK_BY_DTO: Readonly<Record<RiskEnumDto, RiskEnum>> = {
    high: RiskEnum.HIGH,
    favorable: RiskEnum.FAVORABLE,
    moderate: RiskEnum.MODERATE,
    low: RiskEnum.LOW,
};

@injectable()
export class PestMapper {
    map(pestDto: PestDto): Result<Pest, ModelError<typeof Pest>> {
        const pestDtoParsed = PestDtoSchema.safeParse(pestDto);

        if (!pestDtoParsed.success) {
            console.error(pestDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        return Pest.createWithRecovery({
            id: pestDto.id,
            agentId: pestDto.agent_id,
            name: pestDto.name,
            code: PEST_CODE_BY_DTO[pestDto.code],
            risk: pestDto.infection_risk_level
                ? RISK_BY_DTO[pestDto.infection_risk_level]
                : null,
            riskPercentage: pestDto.infection_risk_percentage ?? null,
            measuredAt: pestDto.measured_at,
        });
    }

    mapAvg(
        pestAvgDto: PestAvgDto,
    ): Result<PestAvg, ModelError<typeof PestAvg>> {
        const pestAvgDtoParsed = PestAvgDtoSchema.safeParse(pestAvgDto);

        if (!pestAvgDtoParsed.success) {
            console.error(pestAvgDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        return PestAvg.createWithRecovery({
            avgValue: pestAvgDto.avg_value,
            hourOfDay: pestAvgDto.hour_of_day,
            symbol: pestAvgDto.symbol,
            name: pestAvgDto.name,
            type: pestAvgDto.type,
        });
    }

    mapList(pestsDto: PestsDto): Pests {
        const pestsDtoParsed = PestsDtoSchema.safeParse(pestsDto);

        if (!pestsDtoParsed.success) {
            console.error(pestsDtoParsed.error.issues);
            return Pests.empty();
        }

        const agents = pestsDto.stations_risks.mapNotNull((pestDto) =>
            this.map(pestDto).match(
                (pests) => pests,
                (error) => {
                    if (!error.recuperable) {
                        console.error(
                            "Non-recoverable error mapping pests:",
                            error,
                        );
                    }
                    return null;
                },
            ),
        );

        const agentsAvg = pestsDto.stations_risks_variables_avg.mapNotNull(
            (pestAvgDto) =>
                this.mapAvg(pestAvgDto).match(
                    (pestAvgs) => pestAvgs,
                    (error) => {
                        if (!error.recuperable) {
                            console.error(
                                "Non-recoverable error mapping pests averages:",
                                error,
                            );
                        }
                        return null;
                    },
                ),
        );

        return Pests.create({
            agents,
            agentsAvg,
        }).unwrapOr(Pests.empty());
    }
}
